import { useConfirmationDialog } from "@comps/ui/confirmation-dialog";
import { CompozzError, isCompozzError } from "@dto/CompozzError";
import { TYPE_ITEMS_BUTTON_COLOR, TYPE_ITEMS_BUTTON_CONFIRM_MSG, TYPE_ITEMS_BUTTON_ERROR_MSG, TYPE_ITEMS_BUTTON_LABEL, TYPE_ITEMS_BUTTON_MAPPING, TYPE_ITEMS_BUTTON_METHOD, TYPE_ITEMS_BUTTON_REFRESH, TYPE_ITEMS_BUTTON_REFRESH_ROW, TYPE_ITEMS_BUTTON_SUCCESS_MSG, TYPE_ITEMS_BUTTON_URL } from "@dto/fields";
import { getDarkColor } from "@utils/colors";
import { BASE_URL } from "@utils/constants";
import { toastError, toastSuccess } from "@utils/toasts";
import { TW_Button, getWsButtonStyle, useHover } from "@utils/twClasses";
import { post } from "@utils/utils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDataStore, useModelStore } from "src/store";
import { CellProps } from "./GridCell";
import { newRowWrites } from "./newRowWrites";

export function ButtonCell({ field, item, position }: CellProps) {
    const { t } = useTranslation();
    const wsColor = useModelStore(state => state.wsColor);
    const selectedWorkspace = useModelStore(state => state.selectedWorkspace);
    const selectedView = useModelStore(state => state.selectedView);
    const loadData = useDataStore(state => state.loadData);
    const stopLoadingData = useDataStore(state => state.stopLoadingData);
    const fetchData = useDataStore(state => state.fetchData);
    const fetchLine = useDataStore(state => state.fetchLine);
    const confirmDialog = useConfirmationDialog();
    const [loading, setLoading] = useState(false);

    const label: string = field.typeItems?.get(TYPE_ITEMS_BUTTON_LABEL) || field.name || t("field.button.default.label");
    const baseUrl: string = field.typeItems?.get(TYPE_ITEMS_BUTTON_URL) ?? "";
    const method: string = field.typeItems?.get(TYPE_ITEMS_BUTTON_METHOD) ?? "GET";
    const mapping: Record<string, string> = field.typeItems?.get(TYPE_ITEMS_BUTTON_MAPPING) ?? {};
    const color: string = field.typeItems?.get(TYPE_ITEMS_BUTTON_COLOR) ?? wsColor;
    const hoverColor = getDarkColor(color);
    const successMsg: string = field.typeItems?.get(TYPE_ITEMS_BUTTON_SUCCESS_MSG) || t("field.button.success");
    const errorMsg: string = field.typeItems?.get(TYPE_ITEMS_BUTTON_ERROR_MSG) || t("field.button.error");
    const confirmMsg: string = field.typeItems?.get(TYPE_ITEMS_BUTTON_CONFIRM_MSG) ?? "";
    const refresh: boolean = field.typeItems?.get(TYPE_ITEMS_BUTTON_REFRESH) ?? false;
    const refreshRow: boolean = field.typeItems?.get(TYPE_ITEMS_BUTTON_REFRESH_ROW) ?? false;
    const buttonStyle = useHover(getWsButtonStyle(color), getWsButtonStyle(hoverColor));

    async function handleClick(e: React.MouseEvent<HTMLButtonElement>) {
        e.stopPropagation();
        e.preventDefault();
        if (!baseUrl || loading) return;

        if (confirmMsg) {
            const confirmed = await confirmDialog({
                title: t("field.button.confirm.dialog.title"),
                description: confirmMsg.replace(/\\n/g, '\n'),
                cancelButtonStyle: getWsButtonStyle(wsColor),
                cancelButtonHoverStyle: getWsButtonStyle(hoverColor),
                confirmButtonStyle: getWsButtonStyle(wsColor),
                confirmButtonHoverStyle: getWsButtonStyle(hoverColor),
            });
            if (!confirmed) return;
        }

        setLoading(true);
        loadData();
        try {
            // The called service may have used the record: it counts as an input on the creation row
            const result = await newRowWrites.write([item.key.recordKey], post(`${BASE_URL}/data/proxy/button`, {
                url: baseUrl,
                method: method,
                recordKey: item.key.recordKey ?? "",
                workspaceKey: selectedWorkspace?._key ?? "",
                objectKey: item.key.objectKey ?? "",
                mapping: mapping,
            }));
            if (isCompozzError(result)) {
                const cError = result as CompozzError;
                if (cError.message) {
                    toastError(cError.message);
                } else {
                    toastError(errorMsg);
                }
            } else {
                toastSuccess((result as any)?.message ?? successMsg);
                if (refreshRow && position?.rowId) {
                    await fetchLine(position.rowId, [item.key.recordKey ?? ""], selectedView);
                }
                if (refresh) {
                    loadData();
                    fetchData().finally(() => {
                        stopLoadingData();
                    });
                }
            }
        } catch {
            toastError(errorMsg);
        } finally {
            setLoading(false);
            stopLoadingData();
        }
    }

    return (
        <div className="flex items-center justify-center w-full h-full px-1">
            <button
                className={TW_Button}
                {...buttonStyle}
                disabled={loading || !baseUrl}
                onClick={handleClick}
            >
                {label}
            </button>
        </div>
    );
}
